"use client"

import { Heart, MessageCircle, UserPlus, Megaphone, Star, AtSign } from "lucide-react"
import type { ReactNode } from "react"
import type { NotificationItem as NotificationData, NotificationType } from "@/lib/community/community-api"

export type { NotificationData }

const TYPE_META: Record<NotificationType, { icon: ReactNode; color: string; label: string }> = {
  like: {
    icon: <Heart className="h-3.5 w-3.5" />,
    color: "text-rose-500 bg-rose-500/10",
    label: "赞了你的",
  },
  comment: {
    icon: <MessageCircle className="h-3.5 w-3.5" />,
    color: "text-blue-500 bg-blue-500/10",
    label: "评论了你的",
  },
  reply: {
    icon: <MessageCircle className="h-3.5 w-3.5" />,
    color: "text-blue-500 bg-blue-500/10",
    label: "回复了你的",
  },
  follow: {
    icon: <UserPlus className="h-3.5 w-3.5" />,
    color: "text-primary bg-primary/10",
    label: "关注了你",
  },
  favorite: {
    icon: <Star className="h-3.5 w-3.5" />,
    color: "text-orange-500 bg-orange-500/10",
    label: "收藏了你的",
  },
  system: {
    icon: <Megaphone className="h-3.5 w-3.5" />,
    color: "text-amber-500 bg-amber-500/10",
    label: "系统通知",
  },
  mention: {
    icon: <AtSign className="h-3.5 w-3.5" />,
    color: "text-purple-500 bg-purple-500/10",
    label: "提到了你",
  },
}

const TARGET_LABEL: Record<string, string> = {
  post: "帖子",
  comment: "评论",
}

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diff = Math.max(0, now - then)
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return "刚刚"
  if (mins < 60) return `${mins}分钟前`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}小时前`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}天前`
  return `${Math.floor(days / 30)}个月前`
}

/** 根据通知类型和 targetType 生成可读描述 */
function buildTitle(n: NotificationData): string {
  const meta = TYPE_META[n.type] ?? TYPE_META.system
  const name = n.senderNick || "某用户"

  if (n.type === "follow") {
    return `${name} ${meta.label}`
  }
  if (n.type === "system") {
    return n.title || meta.label
  }
  const target = TARGET_LABEL[n.targetType] ?? "内容"
  return `${name} ${meta.label}${target}`
}

export function NotificationItemView({
  notification,
  onClick,
}: {
  notification: NotificationData
  onClick?: (n: NotificationData) => void
}) {
  const meta = TYPE_META[notification.type] ?? TYPE_META.system
  const avatarUrl = notification.senderAvatar
  const title = buildTitle(notification)

  return (
    <button
      type="button"
      onClick={() => onClick?.(notification)}
      className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors hover:bg-foreground/[0.04] ${
        notification.isRead ? "opacity-60" : ""
      }`}
    >
      {/* 头像 / 图标 */}
      <div className="shrink-0 relative">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            className="h-7 w-7 rounded-full object-cover"
          />
        ) : (
          <div className={`flex items-center justify-center h-7 w-7 rounded-full ${meta.color}`}>
            {meta.icon}
          </div>
        )}
        {/* 右下角类型小图标（有头像时显示） */}
        {avatarUrl && (
          <div className={`absolute -bottom-0.5 -right-0.5 flex items-center justify-center h-3.5 w-3.5 rounded-full ${meta.color} ring-1 ring-background`}>
            <span className="scale-[0.65]">{meta.icon}</span>
          </div>
        )}
      </div>

      {/* 内容 */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground leading-snug line-clamp-1">
          {title}
        </p>
        {notification.content && (
          <p className="text-caption text-muted-foreground leading-relaxed line-clamp-2 mt-0.5">
            {notification.content}
          </p>
        )}
        <span className="text-micro text-muted-foreground/60 mt-1 block">
          {timeAgo(notification.createdAt)}
        </span>
      </div>

      {/* 未读小圆点 */}
      {!notification.isRead && (
        <div className="shrink-0 mt-1.5">
          <div className="h-2 w-2 rounded-full bg-primary" />
        </div>
      )}
    </button>
  )
}
